import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const RemoveEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  
  // Check if this is a back navigation from email capture
  const isBackFromEmail = location.state?.fromEmailCapture === true;
  
  // Get the most recently added product
  const lastAddedProduct = state.lastAddedProduct;
  
  // Use global dispensed state to determine if item is removed
  const isRemoved = state.kiosk.isItemDispensed || isBackFromEmail;

  const handleBack = () => {
    navigate("/empty-accepted");
  };

  const handleAddAnother = () => {
    dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });
    navigate("/add-empty");
  };

  const handleContinue = () => {
    if (state.itemsCount ===0) {
      navigate("/add-empty");
    } else {
      dispatch({ type: "SET_STEP", payload: 6 });
      navigate("/email-capture");
    }
  };

  // Reset dispensed state when entering view (unless coming back from email)
  useEffect(() => {
    if (!isBackFromEmail) {
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });
    }
  }, [dispatch, isBackFromEmail]);

  // Remove item from empties immediately when it's confirmed as removed
  // (but not when coming back from email capture, as it may already be removed)
  useEffect(() => {
    if (isRemoved && lastAddedProduct && !isBackFromEmail && state.kiosk.isItemDispensed) {
      dispatch({ type: 'REMOVE_EMPTY', payload: lastAddedProduct.id });
    }
  }, [isRemoved, lastAddedProduct, dispatch, isBackFromEmail, state.kiosk.isItemDispensed]);

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="place-empty-view"
    >
      {/* Breathing Animation Background - Only show when not removed */}
      {!isRemoved && (
        <motion.div
          className="absolute bottom-[-675px] left-0 w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
          style={{
            background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
            filter: 'blur(200px)',
          }}
          animate={{
            y: [0, -100, 0],          
            opacity: [.25, .75, .25],
            scale: [1, 1.25, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className={`${isRemoved ? "row-span-3" : "row-span-2"} flex items-center justify-center`}>
            <div className="text-center w-full max-w-[920px]">
              <AnimatePresence mode="wait">
                {!isRemoved ? (
                  <motion.div
                    key="remove"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ 
                      duration: 0.4, 
                      ease: [0.4, 0.0, 0.2, 1],
                      opacity: { duration: 0.3 },
                      y: { duration: 0.4 }
                    }}
                  >
                    <Heading 
                      title="Remove empty" 
                      subtitle="Please take your empty from the tray"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="removed"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ 
                      duration: 0.4, 
                      ease: [0.4, 0.0, 0.2, 1],
                      opacity: { duration: 0.3 },
                      y: { duration: 0.4 }
                    }}
                  >
                    <Heading 
                      title="Empty removed" 
                      subtitle="Add another empty or continue"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Row 3 - Product details at bottom */}
          {!isRemoved && (
          <div className="flex items-end justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div className="flex flex-col gap-4 items-center py-4">
                  <h3 className="font-brand-secondary font-normal text-[32px] leading-[42px] text-brand-text-primary text-center">
                    {lastAddedProduct ? lastAddedProduct.name : getCopy('productFallback.defaultProductName')}
                  </h3>
                  <div className="flex items-center justify-center">
                    <span className="font-brand-secondary font-normal text-[24px] leading-[31px] text-brand-text-muted text-center">
                      {lastAddedProduct ? lastAddedProduct.size : getCopy('productFallback.defaultProductSize')}
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          )}
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Conditional Buttons */}
          <AnimatePresence mode="wait">
            {!isRemoved ? (
              <motion.div 
                key="back-button"
                className="flex justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  duration: 0.4, 
                  ease: [0.4, 0.0, 0.2, 1],
                  opacity: { duration: 0.3 },
                  y: { duration: 0.4 }
                }}
              >
                <Button variant="secondary" onClick={handleBack}>
                  Cancel
                </Button>
              </motion.div>
            ) : (
              <motion.div 
                key="action-buttons"
                className="flex flex-row gap-6 lg:gap-10 w-full justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  duration: 0.4, 
                  ease: [0.4, 0.0, 0.2, 1],
                  opacity: { duration: 0.3 },
                  y: { duration: 0.4 }
                }}
              >
                {state.itemsCount > 0 ? (
                <Button variant="secondary" onClick={handleAddAnother}>
                  Add Another
                </Button>
                ) : null}
                <Button variant="primary" onClick={handleContinue} className={state.itemsCount === 0 ? "flex-none" : ""}>
                  Continue
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default RemoveEmptyView;
